import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
// Firebase analytics is initialized in the HTML head
import './config/firebase';

const RELOAD_FLAG_KEY = 'sm_chunk_reload_attempt';

const isChunkLoadError = (err: any): boolean => {
  if (!err) return false;
  const name = err?.name || '';
  const message = err?.message || String(err) || '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk \S+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Unexpected token '<'/i.test(message)
  );
};

const purgeAndReload = async () => {
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, '1');
  } catch {}
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {}
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {}
  window.location.reload();
};

// Catch async chunk failures that happen outside React's render path
// (e.g. React.lazy preloads before Suspense mounts).
const alreadyTried = (() => {
  try { return sessionStorage.getItem(RELOAD_FLAG_KEY) === '1'; } catch { return false; }
})();

window.addEventListener('error', (e) => {
  if (isChunkLoadError(e.error) && !alreadyTried) {
    e.preventDefault();
    purgeAndReload();
  }
});
window.addEventListener('unhandledrejection', (e) => {
  if (isChunkLoadError(e.reason) && !alreadyTried) {
    e.preventDefault();
    purgeAndReload();
  }
});

// Clear the one-shot flag once the page successfully boots so a future
// genuine ChunkLoadError can recover too.
window.addEventListener('load', () => {
  setTimeout(() => {
    try { sessionStorage.removeItem(RELOAD_FLAG_KEY); } catch {}
  }, 5000);
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').then((registration) => {
      // Check for updates on every page load so a deploy is picked up fast.
      registration.update().catch(() => {});

      // When a new SW takes control, reload once to use the new shell.
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing) return;
        refreshing = true;
        window.location.reload();
      });

      // If a new worker is waiting, tell it to skip waiting and take over.
      const promptUpdate = (worker: ServiceWorker | null) => {
        if (!worker) return;
        worker.postMessage({ type: 'SKIP_WAITING' });
      };
      if (registration.waiting) promptUpdate(registration.waiting);
      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            promptUpdate(installing);
          }
        });
      });
    }).catch(() => {
      // Service worker registration failed silently (e.g. offline, restricted env)
    });
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
